import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Grid,
  LinearProgress,
  Pagination,
  Stack,
  Typography,
} from "@mui/material"
import { useRollbar } from "@rollbar/react"
import { motion } from "motion/react"
import { useTranslation } from "react-i18next"
import {
  useClearRefinements,
  useCurrentRefinements,
  useHits,
  useHitsPerPage,
  useInstantSearch,
  usePagination,
  useSearchBox,
} from "react-instantsearch-core"

import useResponsive from "@sap/hooks/useResponsive"

import type { ModelType } from "@/types/algolia"

import LoadingProductCard from "./components/LoadingProductCard"
import ProductCard from "./components/ProductCard"

export default function ItemList() {
  const { hits, results, sendEvent } = useHits<ModelType>()
  const { query } = useSearchBox()
  const { status, error } = useInstantSearch()
  const downMd = useResponsive("down", "md")
  const rollbar = useRollbar()

  const {
    currentRefinement,
    nbPages,
    canRefine: hasMultiplePages,
    refine: setCurrentPage,
  } = usePagination()

  const empty = results?.nbHits === 0 && !results?.__isArtificial
  const isLoading = ["loading", "stalled"].includes(status) && results?.nbHits === 0
  const isFetching = ["loading", "stalled"].includes(status) && (results?.nbHits ?? 0) > 0

  const { t } = useTranslation("items")
  useHitsPerPage({
    items: [{ label: "16 hits per page", value: 16, default: true }],
  })

  const { items: attributes, canRefine, refine: clearRefinement } = useCurrentRefinements()
  const { refine: clearFilters } = useClearRefinements()

  function handleSetPage(page: number) {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "smooth",
    })
    setCurrentPage(page)
  }

  if (status === "error") {
    rollbar.error(error)
    return (
      <Stack
        direction="column"
        sx={{
          alignItems: "center",
          flexGrow: 1,
        }}
      >
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {error?.message || t("SomethingWentWrong")}
          <br />
          {t("IfThisErrorPersists")}
        </Alert>
      </Stack>
    )
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
      }}
    >
      {!downMd && (
        <Stack
          direction="row"
          sx={{
            flexWrap: "wrap",
            gap: 1,
            mb: canRefine ? 2 : 0,
          }}
        >
          {canRefine && <Chip label={t("ClearAll")} onClick={clearFilters} variant="outlined" />}
          {attributes.map((attribute) =>
            attribute.refinements.map((refinement) => {
              const isBoolean = refinement.value === "true" || refinement.value === "false"

              if (isBoolean) {
                return (
                  <Chip
                    key={refinement.value}
                    label={`${t(attribute.label)}`}
                    onDelete={() => clearRefinement(refinement)}
                  />
                )
              }

              return (
                <Chip
                  key={refinement.value}
                  label={`${t(attribute.label)}: ${refinement.label}`}
                  onDelete={() => clearRefinement(refinement)}
                />
              )
            })
          )}
        </Stack>
      )}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: isFetching ? 1 : 0 }}
        transition={{ delay: 0.5, duration: 0.75 }}
        sx={{
          position: "relative",
          height: "4px",
        }}
      >
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
          }}
        />
      </Box>
      <Grid
        container
        spacing={3}
        sx={{
          marginBottom: 3,
        }}
      >
        {hits.map((hit) => (
          <Grid item lg={3} md={4} sm={4} xs={6} key={hit.objectID}>
            <ProductCard
              key={hit.objectID}
              id={hit.objectID}
              title={hit.name}
              imgUrl={hit.image_small}
              brandNew={hit.brand_new}
              category={hit.category}
              satellietOriginals={hit.satelliet_originals}
              onClick={() => sendEvent("click", hit, "ProductCard")}
            />
          </Grid>
        ))}

        {isLoading &&
          [...Array(12)].map((_, i) => (
            <Grid item lg={3} sm={6} xs={12} key={i}>
              <LoadingProductCard />
            </Grid>
          ))}

        {!isLoading && empty && <SearchNotFound query={query} />}
      </Grid>
      <Stack
        spacing={2}
        direction="row"
        sx={{
          justifyContent: "center",
        }}
      >
        <Pagination
          count={nbPages}
          page={currentRefinement + 1}
          color="standard"
          disabled={!hasMultiplePages}
          onChange={(e, value) => handleSetPage(value - 1)}
        />
      </Stack>
    </Box>
  )
}

function SearchNotFound({ query }: { query: string }) {
  const { t } = useTranslation("items")

  return (
    <Grid item xs={12}>
      <Box
        sx={{
          textAlign: "center",
        }}
      >
        <Typography variant="h5" paragraph>
          {t("NoSearchResults")}
        </Typography>

        <Typography variant="body1">
          {!query && t("NoSearchResultsFound")}
          {query && (
            <>
              {" "}
              {t("NoSearchResultsFoundfor")} <strong>&quot;{query}&quot;</strong>
            </>
          )}
          .
          <br />
          {t("TryAnotherSearch")}.
        </Typography>
      </Box>
    </Grid>
  )
}
